// Pure helpers for the driver coverage heat map. No React, no side effects.

import { stateCentroid } from './usStates';

export const COVERAGE_METRICS = [
  {
    id: 'drivers',
    label: 'Drivers',
    short: 'Drivers',
    description: 'All drivers with a home address in the area',
    palette: 'supply',
  },
  {
    id: 'activeDrivers',
    label: 'Active drivers',
    short: 'Active',
    description: 'Drivers able to accept an order right now',
    palette: 'supply',
  },
  {
    id: 'ordersPerActiveDriver',
    label: 'Orders per active driver',
    short: 'Orders / driver',
    description: 'In-range orders divided by active drivers. Higher = under-served.',
    palette: 'gap',
  },
];

// Sequential ramps (light -> dark). Supply is emerald; gap is amber -> red so
// "hot" always means "needs attention".
export const PALETTES = {
  supply: ['#ecfdf5', '#a7f3d0', '#6ee7b7', '#34d399', '#059669', '#065f46'],
  gap: ['#fffbeb', '#fde68a', '#fbbf24', '#f97316', '#dc2626', '#7f1d1d'],
};

export const EMPTY_FILL = '#f4f4f5';
export const NO_DRIVER_DEMAND_FILL = '#fee2e2';

export const metricValue = (row, metricId) => {
  if (!row) return null;
  const value = row[metricId];
  return Number.isFinite(value) ? value : null;
};

// Quantize a metric into `steps` buckets using the non-zero positive values
// of all rows. Returns { thresholds, colorFor(value) } so the map and legend
// share identical breaks.
export function buildScale(rows, metricId, { steps = 6, palette = 'supply' } = {}) {
  const colors = PALETTES[palette] || PALETTES.supply;
  const values = rows
    .map((row) => metricValue(row, metricId))
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);

  if (!values.length) {
    return { thresholds: [], colors, colorFor: () => EMPTY_FILL, max: 0, bucketCount: 0 };
  }

  const max = values[values.length - 1];
  const unique = Array.from(new Set(values));
  const bucketCount = Math.min(steps, unique.length);
  const thresholds = [];
  for (let i = 1; i < bucketCount; i += 1) {
    const idx = Math.floor((i / bucketCount) * (unique.length - 1));
    thresholds.push(unique[idx]);
  }

  const colorFor = (value) => {
    if (!Number.isFinite(value) || value <= 0) return EMPTY_FILL;
    let bucket = 0;
    while (bucket < thresholds.length && value > thresholds[bucket]) bucket += 1;
    // Map bucket index onto the palette so the darkest color is used when
    // there are fewer buckets than palette steps.
    const paletteIdx = Math.round((bucket / Math.max(1, bucketCount - 1)) * (colors.length - 1));
    return colors[paletteIdx];
  };

  return { thresholds, colors, colorFor, max, bucketCount };
}

export const legendLabel = (lo, hi, metricId) => {
  const fmt = (n) => (metricId === 'ordersPerActiveDriver' ? n.toFixed(1) : Math.round(n).toLocaleString('en-US'));
  if (lo === null) return `≤ ${fmt(hi)}`;
  if (hi === null) return `> ${fmt(lo)}`;
  return `${fmt(lo)} – ${fmt(hi)}`;
};

export function buildLegend(scale, metricId) {
  if (!scale || !scale.thresholds) return [];
  const { thresholds, colors, bucketCount = 0 } = scale;
  const items = [];
  for (let bucket = 0; bucket < bucketCount; bucket += 1) {
    const lo = bucket === 0 ? null : thresholds[bucket - 1];
    const hi = bucket < thresholds.length ? thresholds[bucket] : null;
    const paletteIdx = Math.round((bucket / Math.max(1, bucketCount - 1)) * (colors.length - 1));
    items.push({ color: colors[paletteIdx], label: legendLabel(lo, hi, metricId) });
  }
  return items;
}

// Bubble radius for city markers: sqrt scaling clamps extremes.
export function bubbleRadius(value, max, { min = 4, cap = 22 } = {}) {
  if (!Number.isFinite(value) || value <= 0 || !Number.isFinite(max) || max <= 0) return 0;
  return Math.max(min, Math.round(min + (Math.sqrt(value) / Math.sqrt(max)) * (cap - min)));
}

// Place cities without coordinates on their state centroid so they still
// render; mark them so tooltips can disclose the approximation.
export function placeCities(cities = []) {
  return cities
    .map((city) => {
      if (Number.isFinite(city.lat) && Number.isFinite(city.lng)) {
        return { ...city, approximate: false };
      }
      const centroid = stateCentroid(city.state);
      if (!centroid) return null;
      return { ...city, lat: centroid.lat, lng: centroid.lng, approximate: true };
    })
    .filter(Boolean);
}

// "Needs drivers" ranking: demand present with thin or no supply first.
// Rows with orders but zero active drivers rank highest, then by orders per
// active driver, then by raw order volume.
export function rankGaps(rows = [], { limit = 25 } = {}) {
  const withDemand = rows.filter((row) => (row.orders || 0) > 0 || (row.properties || 0) > 0);
  const scored = withDemand.map((row) => {
    const orders = row.orders || 0;
    const active = row.activeDrivers || 0;
    const properties = row.properties || 0;
    const ratio = active > 0 ? orders / active : null;
    const gapScore =
      (active === 0 ? 1_000_000 : 0) + (ratio === null ? orders * 100 : ratio * 100) + properties;
    return { ...row, gapScore, ratio };
  });
  scored.sort((a, b) => b.gapScore - a.gapScore || (b.orders || 0) - (a.orders || 0));
  return scored.slice(0, limit);
}

export const coverageStatus = (row) => {
  const orders = row?.orders || 0;
  const active = row?.activeDrivers || 0;
  if (orders === 0 && active === 0) return { id: 'none', label: 'No activity', tone: 'zinc' };
  if (orders > 0 && active === 0) return { id: 'uncovered', label: 'No active drivers', tone: 'red' };
  if (active > 0 && orders / active >= 10) return { id: 'thin', label: 'Thin coverage', tone: 'amber' };
  if (orders === 0) return { id: 'idle', label: 'Drivers, no orders', tone: 'sky' };
  return { id: 'ok', label: 'Covered', tone: 'emerald' };
};

export const pctChange = (current, previous) => {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev === 0) return cur === 0 ? 0 : null;
  return ((cur - prev) / prev) * 100;
};
